import * as React from 'react';
import { CheckMarkIcon, renderWaitingForSuccessElement, WarningIcon } from '../../../webportal/components/common/Icons';
import { convertMongoObjectIdToDate, formatAmount } from '../../../webportal/utils/utilFunctions';
import { createDefaultApi } from '../../../webportal/utils/createApi';

interface ConfirmModalHeaderProps {
    userEmail: string;
    userId: string;
    activeFilespaces: number;
    allFilespaces: number;
    invoiceAboveZero: number;
    totalPaid: number;
}

interface ConfirmModalBodyProps {
    emailInput: string;
    emailsMatch: boolean;
    handleConfirmEmailInputChanged: (e: any) => void;
    deleteUserAccount: () => void;
    resetDeleteStatusAndClose: () => void;
}

interface ConfirmModalErrorFooterProps {
    errorMessage: string;
}

interface ConfirmModalCloseButtonProps {
    resetDeleteStatusAndClose: () => void;
}

export interface ConfirmModalProps extends ConfirmModalHeaderProps {
    isModalShown: boolean;
    closeModal: () => void;
}

interface ConfirmModalState {
    deleteUserOperationConfirmed: boolean;
    deleteUserOperationStarted: boolean;
    deleteUserOperationFinished: boolean;
    successMessage: string;
    errorMessage: string;
    emailInput: string;
    emailsMatch: boolean;
}

const ModalHeader: React.FC<ConfirmModalHeaderProps> = (
    {
        userEmail,
        userId,
        activeFilespaces,
        allFilespaces,
        invoiceAboveZero,
        totalPaid
    }
) => {
    return (
        <>
            <h2>Are you sure you want to delete this account?</h2>
            
            <div className="box-modal">
                User to be deleted:
                <strong>{userEmail.toLowerCase()}</strong>
            </div>
            
            <div className="box-modal">
                <strong>Date Created: {convertMongoObjectIdToDate(userId).toDateString()}</strong>
                <strong>Number of Active Filespaces: {activeFilespaces}</strong>
                <strong>Number of Filespaces (including deleted ones): {allFilespaces}</strong>
                <strong>Number of Invoices with amount larger than 0: {invoiceAboveZero}</strong>
                <strong>Total Paid amount in USD: {formatAmount(totalPaid, 'usd')}</strong>
            </div>
        </>
    );
};

const ModalBody: React.FC<ConfirmModalBodyProps> = (
    {
        emailInput,
        emailsMatch,
        handleConfirmEmailInputChanged,
        deleteUserAccount,
        resetDeleteStatusAndClose
    }
) => {
    return (
        <>
            <section>
                <p>Please confirm the email of the user.</p>
                <input
                    data-autocomplete="off"
                    type="text"
                    name="email"
                    id="email-delete"
                    value={emailInput}
                    onChange={handleConfirmEmailInputChanged}
                />
            </section>

            <div className="deleteButtons">
                <button disabled={!emailsMatch} className="btn warning" onClick={deleteUserAccount}>Yes</button>
                <button className="btn" onClick={resetDeleteStatusAndClose}>No</button>
            </div>
        </>
    );
};

const ModalLoader: React.FC = () => {
    return (
        renderWaitingForSuccessElement('Starting a user delete operation...')
    );
};

const ModalSuccessFooter: React.FC = () => {
    return (
        <footer>
            <div className="icon active">
                <CheckMarkIcon width="28px" height="20px"/>
                <span className="success">Deleted user in Cognito</span>
            </div>

            <div className="icon active">
                <CheckMarkIcon width="28px" height="20px"/>
                <span className="success">Deleted user in Stripe</span>
            </div>

            <div className="icon active">
                <CheckMarkIcon width="28px" height="20px"/>
                <span className="success">Deleted user in database</span>
            </div>
        </footer>
    );
};

const ModalErrorFooter: React.FC<ConfirmModalErrorFooterProps> = ({errorMessage}) => {
    return (
        <footer>
            <p style={{ color: 'orange' }}>
                {errorMessage}
            </p>
        </footer>
    );
};

const ModalCloseButton: React.FC<ConfirmModalCloseButtonProps> = ({resetDeleteStatusAndClose}) => {
    return (
        <div className="deleteButtons">
            <button className="btn" onClick={resetDeleteStatusAndClose}>Close</button>
        </div>
    );
};

export class UserDeleteConfirmModal extends React.Component<ConfirmModalProps, ConfirmModalState> {
    constructor(props: ConfirmModalProps) {
        super(props);
        this.deleteUserAccount = this.deleteUserAccount.bind(this);
        this.resetDeleteStatusAndClose = this.resetDeleteStatusAndClose.bind(this);
        this.handleConfirmEmailInputChanged = this.handleConfirmEmailInputChanged.bind(this);
        this.state = {
            deleteUserOperationConfirmed: false,
            deleteUserOperationStarted: false,
            deleteUserOperationFinished: false,
            errorMessage: '',
            successMessage: '',
            emailInput: '',
            emailsMatch: false,
        };
    }

    render() {
        const modalClassName = this.props.isModalShown ? 'modalBO show' : 'modalBO';
        return (
            <div
                className={modalClassName}
                id="confirmDeleteModal"
                tabIndex={-1}
                role="dialog"
                aria-labelledby="confirmDeleteModal"
                aria-hidden="true"
            >
                <div>
                    <ModalHeader
                        userEmail={this.props.userEmail}
                        userId={this.props.userId}
                        allFilespaces={this.props.allFilespaces}
                        activeFilespaces={this.props.activeFilespaces}
                        invoiceAboveZero={this.props.invoiceAboveZero}
                        totalPaid={this.props.totalPaid}
                    />

                    <p className="alert alert-danger">
                        <WarningIcon width="24px" height="22px"/>
                        <span>Please note that <strong>this action is irreversible</strong>!</span>
                    </p>

                    {
                        !this.state.deleteUserOperationConfirmed &&
                        <ModalBody 
                            emailInput={this.state.emailInput}
                            emailsMatch={this.state.emailsMatch}
                            handleConfirmEmailInputChanged={this.handleConfirmEmailInputChanged}
                            deleteUserAccount={this.deleteUserAccount}
                            resetDeleteStatusAndClose={this.resetDeleteStatusAndClose}
                        />
                    }

                    {
                        this.state.deleteUserOperationStarted &&
                        <ModalLoader />
                    }

                    {
                        this.state.deleteUserOperationFinished &&
                        <ModalSuccessFooter />
                    }

                    {
                        this.state.errorMessage &&
                        <ModalErrorFooter errorMessage={this.state.errorMessage} />
                    }

                    {
                        (this.state.deleteUserOperationFinished || this.state.errorMessage) &&
                        <ModalCloseButton resetDeleteStatusAndClose={this.resetDeleteStatusAndClose} />
                    }
                </div>
            </div>
        );
    }

    private resetDeleteStatusAndClose(): void {
        this.setState(
            {
                deleteUserOperationConfirmed: false,
                deleteUserOperationStarted: false,
                deleteUserOperationFinished: false,
                errorMessage: '',
                successMessage: ''
            });
        this.props.closeModal();
    }

    private async deleteUserAccount(): Promise<void> {
        this.setState(
            {
                deleteUserOperationStarted: true,
                deleteUserOperationConfirmed: true,
            });

        try {
            const api = await createDefaultApi();
            await api.removeAccount(this.props.userId);
            this.setState(
                {
                    deleteUserOperationStarted: false,
                    deleteUserOperationFinished: true,
                    successMessage: `You have successfully deleted user ${this.props.userEmail}. You can close this window.`
                });
        } catch (e) {
            this.setState(
                {
                    errorMessage: 'Failed to delete this account. Please try again later or contact the development team.',
                    deleteUserOperationStarted: false,
                }
            );
        }
    }

    private handleConfirmEmailInputChanged(e: any): void {
        const trimmedEmail = e.target.value.trim();
        this.setState(
            {
                emailInput: trimmedEmail,
                emailsMatch: trimmedEmail === this.props.userEmail
            }
        );
    }
}



// WEBPACK FOOTER //
// ./src/backoffice/components/user/UserDeleteConfirmModal.tsx